import React, {useCallback, useEffect, useRef, useState} from "react"
import {useTranslation} from "react-i18next"
import {Pagination, Popconfirm} from "antd"
import moment from "moment"
import RichTextEditor, {RichTextEditorRef} from "components/RichTextEditor/RichTextEditor"
import {INITIAL_RTE_CONTENT, isEmptyContent} from "components/RichTextEditor/slate-custom-types"
import {BaseButton, BaseLoading} from "components"
import {handleError, toastError, toastSuccess} from "helpers"
import {studentActivityCommentsService} from "services"
import {StudentActivityComment} from "services/StudentActivityCommentsService"
import {useModel} from "hooks"
import {Descendant} from "slate"
import {Icon} from "components/Icon"
import styles from "./ActivityComments.module.css"

type Props = {
    studentActivityId: number
    readOnly?: boolean
    legacyComment?: string | null
}

const ActivityComments: React.FC<Props> = ({studentActivityId, readOnly = false, legacyComment}) => {
    const {t} = useTranslation(["common"])
    const model = useModel()
    const [comments, setComments] = useState<StudentActivityComment[]>([])
    const [page, setPage] = useState(1)
    const [isLoading, setIsLoading] = useState(false)

    const PAGE_SIZE = 5
    const pagedComments = comments.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
    const [isSaving, setIsSaving] = useState(false)
    const [newComment, setNewComment] = useState<Descendant[]>(INITIAL_RTE_CONTENT)
    const [editingId, setEditingId] = useState<number | null>(null)
    const [editingContent, setEditingContent] = useState<Descendant[]>(INITIAL_RTE_CONTENT)
    const newCommentEditorRef = useRef<RichTextEditorRef>(null)

    const loadComments = useCallback(async () => {
        setIsLoading(true)
        try {
            const result = await studentActivityCommentsService.list(studentActivityId)
            setComments(result.data)
            setPage(1)
        } catch (error) {
            handleError(error)
        } finally {
            setIsLoading(false)
        }
    }, [studentActivityId])

    useEffect(() => {
        loadComments()
    }, [loadComments])

    const handleAddComment = async () => {
        if (isEmptyContent(newComment)) {
            toastError(t("common:validation.cantEmpty", {field: "Comment"}))
            return
        }
        setIsSaving(true)
        try {
            const created = await studentActivityCommentsService.create(studentActivityId, JSON.stringify(newComment))
            setComments((prev) => [...prev, created])
            setNewComment(INITIAL_RTE_CONTENT)
            toastSuccess(t("activityPopup.commentAdded", "Comment added"))
        } catch (error) {
            handleError(error)
        } finally {
            setIsSaving(false)
        }
    }

    const handleStartEdit = (comment: StudentActivityComment) => {
        setEditingId(comment.studentActivityCommentId)
        try {
            setEditingContent(JSON.parse(comment.comment))
        } catch {
            setEditingContent(INITIAL_RTE_CONTENT)
        }
    }

    const handleCancelEdit = () => {
        setEditingId(null)
        setEditingContent(INITIAL_RTE_CONTENT)
    }

    const handleSaveEdit = async (commentId: number) => {
        if (isEmptyContent(editingContent)) {
            toastError(t("common:validation.cantEmpty", {field: "Comment"}))
            return
        }
        setIsSaving(true)
        try {
            const updated = await studentActivityCommentsService.edit(commentId, JSON.stringify(editingContent))
            setComments((prev) => prev.map((c) => (c.studentActivityCommentId === commentId ? updated : c)))
            setEditingId(null)
        } catch (error) {
            handleError(error)
        } finally {
            setIsSaving(false)
        }
    }

    const handleDelete = async (commentId: number) => {
        try {
            await studentActivityCommentsService.deleteComment(commentId)
            setComments((prev) => {
                const next = prev.filter((c) => c.studentActivityCommentId !== commentId)
                const maxPage = Math.max(1, Math.ceil(next.length / PAGE_SIZE))
                setPage((p) => Math.min(p, maxPage))
                return next
            })
        } catch (error) {
            handleError(error)
        }
    }

    const parseContent = (raw: string): Descendant[] => {
        try {
            return JSON.parse(raw)
        } catch {
            return INITIAL_RTE_CONTENT
        }
    }

    return (
        <div className={styles.wrap}>
            <div className={styles.commentList}>
                {isLoading && <BaseLoading isShow />}
                {!!legacyComment && (
                    <div className={`${styles.comment} ${styles.legacyComment}`}>
                        <div className={styles.commentHeader}>
                            <span className={styles.timestamp}>Legacy comment (imported)</span>
                        </div>
                        <div className={styles.legacyText}>{legacyComment}</div>
                    </div>
                )}
                {!isLoading && comments.length === 0 && !legacyComment && (
                    <div className={styles.empty}>No comments yet</div>
                )}
                {pagedComments.map((comment) => {
                    const isOwn =
                        comment.createdProfileId === model.profileId ||
                        (comment.createdProfileId == null && comment.updatedProfileId === model.profileId)
                    const isEditing = editingId === comment.studentActivityCommentId
                    const wasEdited = comment.updatedAt !== comment.createdAt

                    return (
                        <div key={comment.studentActivityCommentId} className={styles.comment}>
                            <div className={styles.commentHeader}>
                                <span className={styles.author}>{comment.createdByProfile?.fullName ?? "Unknown"}</span>
                                <span className={styles.timestamp}>
                                    {moment(comment.createdAt).format("MMM D, YYYY h:mm A")}
                                    {wasEdited && <span className={styles.edited}> (edited)</span>}
                                </span>
                                {isOwn && !readOnly && (
                                    <span className={styles.actions}>
                                        <span className={styles.actionBtn} onClick={() => handleStartEdit(comment)}>
                                            <Icon icon="EDIT" size={16} color="#888" />
                                        </span>
                                        <Popconfirm
                                            title="Delete this comment?"
                                            onConfirm={() => handleDelete(comment.studentActivityCommentId)}
                                            okText="Delete"
                                            cancelText="Cancel"
                                            okButtonProps={{danger: true}}>
                                            <span className={styles.actionBtn}>
                                                <Icon icon="DELETE" size={16} color="#888" />
                                            </span>
                                        </Popconfirm>
                                    </span>
                                )}
                            </div>
                            {isEditing ? (
                                <div className={styles.editArea}>
                                    <RichTextEditor value={editingContent} onChange={setEditingContent} />
                                    <div className={styles.editActions}>
                                        <BaseButton
                                            title="Save"
                                            onClick={() => handleSaveEdit(comment.studentActivityCommentId)}
                                            loading={isSaving}
                                        />
                                        <BaseButton title="Cancel" variant="secondary" onClick={handleCancelEdit} />
                                    </div>
                                </div>
                            ) : (
                                <RichTextEditor value={parseContent(comment.comment)} readOnly />
                            )}
                        </div>
                    )
                })}
                {comments.length > PAGE_SIZE && (
                    <Pagination
                        current={page}
                        pageSize={PAGE_SIZE}
                        total={comments.length}
                        onChange={setPage}
                        showSizeChanger={false}
                        className={styles.pagination}
                    />
                )}
            </div>

            {!readOnly && (
                <div className={styles.newComment}>
                    <RichTextEditor
                        ref={newCommentEditorRef}
                        value={newComment}
                        onChange={setNewComment}
                        placeholder="Add a comment..."
                    />
                    <BaseButton
                        title="Add Comment"
                        onClick={handleAddComment}
                        loading={isSaving}
                        className={styles.submitBtn}
                    />
                </div>
            )}
        </div>
    )
}

export default ActivityComments
